import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from '../../i18n';
import { translateGames } from '../../i18n/helpers';
import { GameConfig } from '../../config/types';
import { config } from '../../config';
import { isMonthlyFreeGame } from '../../config/monthlyFreeGames';
import { GamePremiumBadge } from './GamePremiumBadge';

interface GameCarouselProps {
  games: GameConfig[];
  currentGameType?: string;
  votes?: Record<string, number>;
  onSelectGame: (gameType: string) => void;
  onPremiumClick?: () => void;
  isPremiumRoom?: boolean;
}

// Helper functions
const getVisibleColumns = (): number => {
  if (window.innerWidth <= 768) return 1; // Mobile: 1 column (1 tile per slide)
  return 4; // Tablet/Desktop: 4 columns (8 tiles)
};

const isMobileCarousel = (visibleColumns: number): boolean => visibleColumns === 1;

const CarouselChevron: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
  <svg
    className="carousel-arrow-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {direction === 'left' ? (
      <path d="M15 6l-6 6 6 6" />
    ) : (
      <path d="M9 6l6 6-6 6" />
    )}
  </svg>
);

const calculateTotalColumns = (gamesCount: number, visibleColumns: number): number => {
  if (isMobileCarousel(visibleColumns)) {
    return gamesCount;
  }

  const baseColumns = Math.ceil(gamesCount / 2);
  const expandedColumns = Math.max(
    Math.ceil((gamesCount - 1) / 2) + 2,
    gamesCount - 1
  );
  return Math.max(visibleColumns, baseColumns, expandedColumns);
};

const calculateTilePosition = (
  index: number,
  gamesCount: number,
  expandedIndex: number | null,
  visibleColumns: number
): { finalCol: number; baseRow: number } => {
  if (isMobileCarousel(visibleColumns)) {
    return { finalCol: index, baseRow: 0 };
  }

  const baseColumns = Math.ceil(gamesCount / 2);
  const baseRow = Math.floor(index / baseColumns);
  const baseCol = index % baseColumns;
  
  if (expandedIndex === null) {
    return { finalCol: baseCol, baseRow };
  }
  
  const expandedBaseCol = expandedIndex % baseColumns;
  const expandedBaseRow = Math.floor(expandedIndex / baseColumns);
  const isExpanded = expandedIndex === index;
  
  if (isExpanded) {
    return { finalCol: baseCol, baseRow };
  }
  
  if (baseRow === expandedBaseRow) {
    // Same row: tiles before expanded stay, tiles after shift by 1
    return { finalCol: baseCol < expandedBaseCol ? baseCol : baseCol + 1, baseRow };
  }
  
  // Other row: tiles before expanded stay, tiles at/after shift by 2
  return { finalCol: baseCol < expandedBaseCol ? baseCol : baseCol + 2, baseRow };
};

const scrollTileIntoView = (
  tile: HTMLDivElement,
  container: HTMLDivElement,
  delay: number = 100
): void => {
  setTimeout(() => {
    const tileRect = tile.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    
    const tileLeft = tileRect.left - containerRect.left + scrollLeft;
    const tileRight = tileLeft + tileRect.width;
    const containerRight = scrollLeft + containerRect.width;
    
    if (tileLeft < scrollLeft) {
      container.scrollTo({ left: tileLeft - 20, behavior: 'smooth' });
    } else if (tileRight > containerRight) {
      container.scrollTo({ left: tileRight - containerRect.width + 20, behavior: 'smooth' });
    }
  }, delay);
};

const getTileClassName = (isExpanded: boolean, isSelected: boolean): string => {
  const classes = ['game-tile'];
  classes.push(isExpanded ? 'game-tile-expanded' : 'game-tile-collapsed');
  if (isSelected) classes.push('game-tile-selected');
  return classes.join(' ');
};

const parseExtendedDescriptionBullets = (text: string): string[] =>
  text.split(' • ').map((part) => part.trim()).filter(Boolean);

const getGameImageFileName = (gameId: string, appId: string): string | null => {
  if (appId === 'blitz') {
    const pathMap: Record<string, string> = {
      'what-could-it-be': 'whatCould/whatCouldItBe.png',
    };
    if (pathMap[gameId]) {
      return pathMap[gameId];
    }
    const folderMap: Record<string, string> = {
      'speed-challenge': 'speed',
      'word-puzzle': 'word',
      'picture-perfect': 'picture',
      'close-or-not': 'close',
      'how-many': 'close',
      'fact-or-myth': 'factormyth',
      'trivia': 'trivia',
      'almost-genius': 'almost',
      'pair-matching': 'pairIT',
    };
    const folderName = folderMap[gameId] || gameId;
    return `${folderName}/${folderName}.png`;
  }

  if (appId === 'party') {
    const fileMap: Record<string, string> = {
      'drinking-game': 'drink.jpg',
      'hot-takes': 'agree.jpg',
      'would-you-rather': 'would.jpg',
      'are-you-drunk': 'areyou.jpg',
      'rapid-fire': 'rapid.jpg',
      'stoned-yet': 'stoned.jpg',
      'kinky-or-not': 'kinky.jpeg',
      'play-it-loud': 'playit.jpg',
      'party-pair-matching': 'pairIt.png',
      'guess-what': 'guessWhat.jpg',
    };
    return fileMap[gameId] || null;
  }

  if (appId === 'team') {
    const fileMap: Record<string, string> = {
      'agile-quiz': 'agile.jpg',
      'tech-trivia': 'techtrivia.jpg',
      'business-brain-teasers': 'business.jpg',
      'its-funny': 'funny.jpg',
      'before-or-after': 'beforeafter.webp',
      'team-pair-matching': 'pairIt.jpg',
      'clue-in': 'clueIn.jpg',
    };
    return fileMap[gameId] || null;
  }

  if (appId === 'family') {
    const fileMap: Record<string, string> = {
      'family-trivia': 'family-trivia-thumb.png',
      'animal-quiz': 'animal-quiz-thumb.png',
      'wonder-world': 'wonder-world-thumb.png',
      'what-am-i': 'who-am-i-thumb.png',
      'family-pair-matching': 'pair-it-thumb.png',
      'how-many': 'how-many-thumb.png',
    };
    return fileMap[gameId] || null;
  }

  return null;
};

const getGameImagePath = (gameId: string): string | null => {
  const appFolder = config.appId || 'blitz';
  const fileName = getGameImageFileName(gameId, appFolder);

  return fileName ? `/assets/games/${appFolder}/${fileName}` : null;
};

const getSelectedAccent = (): { color: string; rgb: string } => {
  if (config.appId === 'blitz') {
    return {
      color: '#ef4444',
      rgb: '239, 68, 68',
    };
  }

  return {
    color: 'var(--color-primary)',
    rgb: 'var(--color-primary-rgb)',
  };
};

const GameCarousel: React.FC<GameCarouselProps> = ({
  games,
  currentGameType,
  votes = {},
  onSelectGame,
  onPremiumClick,
  isPremiumRoom = false,
}) => {
  const { t } = useTranslation();
  const translatedGames = useMemo(() => translateGames(games), [games, t]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [visibleColumns, setVisibleColumns] = useState(getVisibleColumns());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tileRefs = useRef<(HTMLDivElement | null)[]>([]);
  const selectedAccent = getSelectedAccent();

  useEffect(() => {
    const updateVisibleColumns = () => setVisibleColumns(getVisibleColumns());
    updateVisibleColumns();
    window.addEventListener('resize', updateVisibleColumns);
    return () => window.removeEventListener('resize', updateVisibleColumns);
  }, []);

  // Update arrow visibility based on scroll position
  const updateArrowVisibility = () => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    // Initial check and setup
    const checkVisibility = () => {
      updateArrowVisibility();
    };
    
    // Use a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkVisibility, 100);
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', updateArrowVisibility);
      window.addEventListener('resize', updateArrowVisibility);
      return () => {
        clearTimeout(timeoutId);
        container.removeEventListener('scroll', updateArrowVisibility);
        window.removeEventListener('resize', updateArrowVisibility);
      };
    }
    
    return () => clearTimeout(timeoutId);
  }, [visibleColumns, translatedGames.length]);

  const totalColumns = calculateTotalColumns(translatedGames.length, visibleColumns);

  // Sync expandedIndex with currentGameType when screen size changes or game selection changes
  useEffect(() => {
    const isMobile = isMobileCarousel(visibleColumns);
    
    if (isMobile) {
      // On mobile: clear expansion but keep selection (via currentGameType)
      setExpandedIndex(null);
    } else {
      // On larger screens: sync expandedIndex with currentGameType
      if (currentGameType) {
        const gameIndex = translatedGames.findIndex(game => game.id === currentGameType);
        if (gameIndex !== -1) {
          setExpandedIndex(gameIndex);
        }
      } else {
        setExpandedIndex(null);
      }
    }
  }, [visibleColumns, currentGameType, translatedGames]);

  useEffect(() => {
    if (expandedIndex !== null && containerRef.current) {
      const tile = tileRefs.current[expandedIndex];
      const container = containerRef.current;
      if (tile) {
        scrollTileIntoView(tile, container);
        // Update arrow visibility after scrolling
        setTimeout(updateArrowVisibility, 400);
      }
    }
  }, [expandedIndex]);

  const handleTileClick = (index: number, gameId: string) => {
    const game = translatedGames[index];
    const isPremiumGame = game.premium === true;
    const isMonthlyFree = isPremiumGame && !isPremiumRoom && isMonthlyFreeGame(gameId);
    const isLockedPremium = isPremiumGame && !isPremiumRoom && !isMonthlyFree;

    if (isLockedPremium && onPremiumClick) {
      onPremiumClick();
      return;
    }

    const isMobile = isMobileCarousel(visibleColumns);
    
    if (isMobile) {
      // On mobile: only select, don't expand
      onSelectGame(gameId);
    } else {
      // On larger screens: toggle expansion
      if (expandedIndex === index) {
        setExpandedIndex(null);
      } else {
        setExpandedIndex(index);
        onSelectGame(gameId);
      }
    }
  };

  const scrollLeft = () => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    if (isMobileCarousel(visibleColumns)) {
      const firstTile = tileRefs.current[0];
      const gap = parseFloat(
        getComputedStyle(container.querySelector('.game-tiles-grid') || container).columnGap || '0'
      ) || 0;
      const step = firstTile ? firstTile.offsetWidth + gap : container.clientWidth;
      const currentIndex = Math.round(container.scrollLeft / step);
      container.scrollTo({ left: Math.max(0, currentIndex - 1) * step, behavior: 'smooth' });
    } else {
      const columnWidth = container.clientWidth / visibleColumns;
      container.scrollBy({ left: -columnWidth, behavior: 'smooth' });
    }
    // Update arrow visibility after scroll animation
    setTimeout(updateArrowVisibility, 300);
  };

  const scrollRight = () => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    if (isMobileCarousel(visibleColumns)) {
      const firstTile = tileRefs.current[0];
      const gap = parseFloat(
        getComputedStyle(container.querySelector('.game-tiles-grid') || container).columnGap || '0'
      ) || 0;
      const step = firstTile ? firstTile.offsetWidth + gap : container.clientWidth;
      const currentIndex = Math.round(container.scrollLeft / step);
      const maxIndex = Math.max(0, translatedGames.length - 1);
      container.scrollTo({ left: Math.min(maxIndex, currentIndex + 1) * step, behavior: 'smooth' });
    } else {
      const columnWidth = container.clientWidth / visibleColumns;
      container.scrollBy({ left: columnWidth, behavior: 'smooth' });
    }
    // Update arrow visibility after scroll animation
    setTimeout(updateArrowVisibility, 300);
  };

  return (
    <div className="game-carousel-root">
      <div className="game-carousel-row">
        <button
          type="button"
          onClick={canScrollLeft ? scrollLeft : undefined}
          className="carousel-arrow carousel-arrow-left"
          aria-label={t('gameplay.carousel.scrollLeft')}
          disabled={!canScrollLeft}
        >
          <CarouselChevron direction="left" />
        </button>

        <div className="game-carousel-track">
          <div className="game-tiles-container" ref={containerRef}>
            <div
              className="game-tiles-grid"
              style={{
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--carousel-visible-cols' as any]: visibleColumns,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--carousel-total-cols' as any]: totalColumns,
                gridTemplateColumns: `repeat(${totalColumns}, minmax(10.75rem, 1fr))`,
                width: `calc(100% * ${totalColumns} / ${visibleColumns} + ${totalColumns - 1} * var(--lobby-carousel-gap, 1rem) - (${visibleColumns} - 1) * var(--lobby-carousel-gap, 1rem) * ${totalColumns} / ${visibleColumns})`,
              }}
            >
        {translatedGames.map((game, index) => {
          const isMobile = isMobileCarousel(visibleColumns);
          // On mobile, never expand - only select
          const isExpanded = !isMobile && expandedIndex === index;
          const isSelected = currentGameType === game.id;
          const voteCount = votes[game.id] || 0;
          const { finalCol, baseRow } = calculateTilePosition(
            index,
            translatedGames.length,
            isMobile ? null : expandedIndex,
            visibleColumns
          );
          
          // Check if game is premium from config (not based on index)
          const isPremium = game.premium === true;
          
          // On larger screens, expanded tile spans 2 columns × 2 rows (4 tiles total)
          const expandedColumnSpan = 2;
          
          const gridColumn = isExpanded 
            ? `${finalCol + 1} / ${finalCol + 1 + expandedColumnSpan}`
            : `${finalCol + 1} / ${finalCol + 2}`;
          
          // For expanded tiles: always span rows 1-2 (fixed position)
          const gridRow = isExpanded 
            ? '1 / 3' // Always span both rows (rows 1 and 2)
            : `${baseRow + 1} / ${baseRow + 2}`;

          const tileImage = getGameImagePath(game.id);
          const isMonthlyFree = isPremium && !isPremiumRoom && isMonthlyFreeGame(game.id);
          const hasImage = Boolean(tileImage);
          const tileClasses = [
            getTileClassName(isExpanded, isSelected),
            hasImage ? 'game-tile--has-image' : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={game.id}
              ref={(el) => { tileRefs.current[index] = el; }}
              className={tileClasses}
              onClick={() => handleTileClick(index, game.id)}
              style={{
                gridColumn,
                gridRow,
                position: 'relative',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--game-accent' as any]: game.color,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--game-selection-accent' as any]: selectedAccent.color,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--game-selection-accent-rgb' as any]: selectedAccent.rgb,
              }}
              data-game-id={game.id}
            >
              {isPremium && (
                <GamePremiumBadge
                  isMonthlyFree={isMonthlyFree}
                  onPremiumClick={onPremiumClick}
                />
              )}
              <div className="game-tile-content-wrapper">
                {!isExpanded ? (
                  <div className="game-tile-compact">
                    <div className="game-tile-compact-top">
                      {tileImage && (
                        <div className="game-tile-thumb" aria-hidden="true">
                          <img src={tileImage} alt="" />
                          <div className="game-tile-thumb-overlay" />
                        </div>
                      )}
                      <div className="game-tile-compact-copy">
                        <div className="game-tile-header">
                          <h3 className="game-tile-title">{game.name}</h3>
                        </div>
                      </div>
                    </div>
                    <p className="game-tile-description">{game.description}</p>
                  </div>
                ) : (
                  <div className="game-tile-expanded-content">
                    <div className="game-tile-expanded-top">
                      {tileImage && (
                        <div className="game-tile-hero" aria-hidden="true">
                          <img src={tileImage} alt="" />
                        </div>
                      )}
                      <div className="game-tile-expanded-copy">
                        <div className="game-tile-expanded-header">
                          <h3 className="game-tile-title-expanded">{game.name}</h3>
                        </div>
                        <p className="game-tile-description-expanded game-tile-description-expanded--subtitle">
                          {game.description}
                        </p>
                        {voteCount > 0 && (
                          <div className="game-tile-votes">
                            {voteCount} {t(voteCount === 1 ? 'common.vote_one' : 'common.vote_other')}
                          </div>
                      )}
                      </div>
                    </div>
                    <div className="game-tile-expanded-details">
                      {game.extendedDescription && (
                        <ul className="game-tile-extended-bullets" aria-label={t('gameplay.carousel.gameDetails')}>
                          {parseExtendedDescriptionBullets(game.extendedDescription).map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
          </div>
        </div>

        <button
          type="button"
          onClick={canScrollRight ? scrollRight : undefined}
          className="carousel-arrow carousel-arrow-right"
          aria-label={t('gameplay.carousel.scrollRight')}
          disabled={!canScrollRight}
        >
          <CarouselChevron direction="right" />
        </button>
      </div>
    </div>
  );
};

export default GameCarousel;
